"use client";

import { Suspense, useEffect, useMemo } from "react";

import { useCompanyContext } from "@/context/CompanyContext";
import { PDFConfigProvider } from "@/context/PDFConfigContext";
import { getTemplate } from "@/components/pdf/templates";
import { loaFormDataToPdfConfig } from "@/components/pdf/templates/loaFormDataAdapter";

export interface LoaDocumentPreviewProps {
  templateKey: string | undefined;
  formData: Record<string, unknown>;
  clientId?: string | null;
  className?: string;
  testId?: string;
}

/** Renders a letter-of-authority from saved form data and a React PDF template key. */
export function LoaDocumentPreview({
  templateKey,
  formData,
  clientId,
  className,
  testId = "loa-document-preview",
}: LoaDocumentPreviewProps) {
  const { companyAssets, loadCompanyConfig } = useCompanyContext();
  const dataOverride = useMemo(() => loaFormDataToPdfConfig(formData), [formData]);
  const TemplateComponent = templateKey ? getTemplate(templateKey) : null;

  useEffect(() => {
    if (clientId) loadCompanyConfig(clientId);
  }, [clientId, loadCompanyConfig]);

  if (!templateKey || !TemplateComponent) {
    return (
      <div className="rounded-xl bg-white/[0.02] px-4 py-6 text-sm text-slate-400 ring-1 ring-white/5">
        Preview unavailable — missing template key.
      </div>
    );
  }

  return (
    <PDFConfigProvider configId="">
      <div
        className={`loa-document-preview flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white text-slate-900 shadow-lg ${className ?? ""}`}
        data-testid={testId}
        style={{ isolation: "isolate", contain: "layout style" }}
      >
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
          <div className="select-none [&_*]:pointer-events-none">
            <Suspense fallback={<p className="p-6 text-sm text-slate-500">Loading preview…</p>}>
              <TemplateComponent companyAssets={companyAssets} dataOverride={dataOverride} />
            </Suspense>
          </div>
        </div>
      </div>
    </PDFConfigProvider>
  );
}
